
import { LAYER } from '../../../../components/layers';
import { createPortal } from 'react-dom';
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  ChevronUp,
  Minus,
  RotateCcw,
  Trash2,
  Upload,

  X,
} from 'lucide-react';
import { Button } from '../../../../components/Button';
import { CountBadge } from '../../../../components/CountBadge';

import { Tooltip } from '../../../../components/Tooltip';
import { Select } from '../../../../components/inputs/Select';
import { TextField } from '../../../../components/inputs/TextField';
import { StatusText } from '../../../../components/table/StatusText';
import { formatBytes } from '../../../../lib/formatBytes';
import { MAX_UPLOAD_BYTES, useRepositoryUpload } from './RepositoryUploadContext';

/**
 * The upload queue's dock.
 *
 * **Expanded it is a centred panel over a dimmed backdrop; minimized it is a pill in the
 * bottom-right corner.** Clicking the backdrop MINIMIZES rather than closes — the same
 * affordance as the minus in the header — because the queue holds work in progress and a
 * stray click must not throw a batch away. Closing is the X, and it says so.
 *
 * This is GRC's Documents workspace dock, built from this product's own components.
 */
export function UploadDock() {
  const {
    batches,
    activeBatchId,
    open,
    minimized,
    outstanding,
    classifications,
    filedUnder,
    setActiveBatchId,
    setMinimized,
    setOrder,
    updateItem,
    moveItem,
    removeItem,
    uploadOne,
    uploadAll,
    closeBatch,
    clearAll,
  } = useRepositoryUpload();

  if (!open) {
    return null;
  }

  const batch = batches.find((b) => b.id === activeBatchId) ?? batches[batches.length - 1];
  const uploading = batches.flatMap((b) => b.items).filter((i) => i.status === 'uploading').length;
  const sendable = batch?.items.filter((i) => i.status === 'pending' || i.status === 'error') ?? [];

  if (minimized) {
    return createPortal(
      <div className={`fixed bottom-4 right-4 ${LAYER.DOCK}`}>
        <button
          type="button"
          id="repository-upload-restore"
          onClick={() => setMinimized(false)}
          className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-slate-700 shadow-raised"
        >
          <Upload className="h-4 w-4 text-slate-500" aria-hidden="true" />
          <span className="text-sm font-medium">Uploads</span>
          {outstanding > 0 ? <CountBadge tone="blue" count={outstanding} /> : null}
          <ChevronUp className="h-4 w-4 text-slate-400" aria-hidden="true" />
        </button>
      </div>,
      document.body,
    );
  }

  return createPortal(
    <>
      <div className={`fixed inset-0 ${LAYER.DOCK} flex items-center justify-center p-4`}>
        {/*
          Click-outside MINIMIZES rather than closes. The queue is work in progress, and the
          one gesture people make by accident must not be the destructive one.
        */}
        <div
          className="absolute inset-0 bg-slate-900/30"
          onClick={() => setMinimized(true)}
          aria-hidden="true"
        />

        <div
          role="dialog"
          aria-label="Upload documents"
          /*
            Wider than GRC's 1100px — product call, 17 Sep 2026. Seven columns, and the
            filename is both the widest value and the one the reader is editing; at 1100 it
            was the column that gave way. `max-w` still keeps it inside the viewport, so a
            laptop gets the same panel one gutter narrower rather than a horizontal scroll.
          */
          className="relative flex max-h-[calc(100vh-3rem)] w-[1400px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-panel border border-slate-200 bg-white shadow-raised"
        >
          <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3.5">
            <h2 className="text-sm font-medium text-slate-900">
              Upload documents
              {uploading > 0 ? (
                <span className="ml-2 text-xs font-normal text-slate-500">
                  {uploading} uploading…
                </span>
              ) : null}
            </h2>

            <div className="flex items-center gap-2">
              {batch && sendable.length > 1 ? (
                <Button
                  id="repository-upload-all"
                  variant="primary"
                  onClick={() => void uploadAll(batch.id)}
                  leading={<Upload className="h-4 w-4" aria-hidden="true" />}
                >
                  Upload all
                </Button>
              ) : null}

              <Tooltip label="Minimize">
                <button
                  type="button"
                  id="repository-upload-minimize"
                  onClick={() => setMinimized(true)}
                  aria-label="Minimize"
                  className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                >
                  <Minus className="h-4 w-4" aria-hidden="true" />
                </button>
              </Tooltip>

              <Tooltip label="Close and discard every queued file">
                <button
                  type="button"
                  id="repository-upload-close"
                  onClick={clearAll}
                  aria-label="Close and discard every queued file"
                  className="rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </button>
              </Tooltip>
            </div>
          </div>

          {/* Batches. Each drop or selection opens its own, so one queue can target several folders. */}
          <div className="flex items-center overflow-x-auto border-b border-slate-100 bg-slate-50 modern-scrollbar">
            {batches.map((entry) => {
              const active = entry.id === batch?.id;
              const done = entry.items.filter((i) => i.status === 'success').length;
              const failed = entry.items.filter((i) => i.status === 'error').length;

              return (
                <div
                  key={entry.id}
                  className={`flex shrink-0 cursor-pointer select-none items-center gap-2 border-r border-slate-100 px-4 py-2.5 ${
                    active ? '-mb-px border-b-2 border-b-blue-500 bg-white' : 'hover:bg-slate-100'
                  }`}
                  onClick={() => setActiveBatchId(entry.id)}
                >
                  <span
                    className={`whitespace-nowrap text-xs ${active ? 'font-medium text-slate-900' : 'text-slate-600'}`}
                  >
                    {entry.label}
                  </span>

                  <span className="font-mono text-[10px] tabular-nums text-slate-400">
                    {done}/{entry.items.length}
                  </span>

                  {failed > 0 ? <CountBadge tone="red" count={failed} /> : null}

                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      closeBatch(entry.id);
                    }}
                    aria-label={`Close ${entry.label}`}
                    className="rounded p-0.5 text-slate-400 hover:bg-slate-200 hover:text-slate-600"
                  >
                    <X className="h-3 w-3" aria-hidden="true" />
                  </button>
                </div>
              );
            })}
          </div>

          {batch ? (
            <>
              <div className="flex items-center gap-2 border-b border-slate-100 px-4 py-2">
                <span className="text-xs text-slate-500">Upload order</span>

                <div className="flex overflow-hidden rounded-lg border border-slate-200">
                  {(['simultaneous', 'sequential'] as const).map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      id={`repository-upload-order-${mode}`}
                      onClick={() => setOrder(batch.id, mode)}
                      aria-pressed={batch.order === mode}
                      className={`px-3 py-1 text-xs capitalize ${
                        batch.order === mode
                          ? 'bg-blue-50 font-medium text-slate-900'
                          : 'bg-white text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      {mode}
                    </button>
                  ))}
                </div>

                {batch.order === 'sequential' ? (
                  <span className="text-[11px] text-slate-400">
                    One at a time, in the order listed. Use the arrows to reorder.
                  </span>
                ) : null}
              </div>

              <div className="max-h-[55vh] overflow-auto modern-scrollbar">
                <table className="min-w-full">
                  <thead className="sticky top-0 z-10 bg-slate-100">
                    <tr className="text-left text-[9px] uppercase tracking-wider text-gray-600 [&>th]:font-normal">
                      {batch.order === 'sequential' ? (
                        <th className="w-14 px-2 py-3 text-center">#</th>
                      ) : null}
                      {/*
                        No DEPARTMENT column. The reference's queue has one per row; here the
                        department is the uploader's own with SYS as the floor — product call,
                        18 Sep 2026 — so it is stated once in the footer and never asked.
                      */}
                      <th className="min-w-[240px] px-4 py-3">Filename</th>
                      <th className="w-48 px-4 py-3">Classification</th>
                      <th className="w-24 px-4 py-3">Size</th>
                      <th className="w-32 px-4 py-3">Status</th>
                      <th className="w-20 px-4 py-3 text-right">Action</th>
                    </tr>
                  </thead>

                  <tbody>
                    {batch.items.map((item, index) => {
                      const locked = item.status === 'uploading' || item.status === 'success';

                      return (
                        <tr key={item.id} className="border-t border-slate-100 align-middle">
                          {batch.order === 'sequential' ? (
                            <td className="w-14 px-2 py-2.5">
                              <div className="flex flex-col items-center gap-0.5">
                                <span className="font-mono text-xs tabular-nums text-slate-400">
                                  {index + 1}
                                </span>
                                <div className="flex flex-col">
                                  <button
                                    type="button"
                                    onClick={() => moveItem(batch.id, item.id, 'up')}
                                    disabled={index === 0}
                                    aria-label="Move up"
                                    className="p-0.5 text-slate-400 hover:text-slate-700 disabled:opacity-20"
                                  >
                                    <ArrowUp className="h-3 w-3" aria-hidden="true" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => moveItem(batch.id, item.id, 'down')}
                                    disabled={index === batch.items.length - 1}
                                    aria-label="Move down"
                                    className="p-0.5 text-slate-400 hover:text-slate-700 disabled:opacity-20"
                                  >
                                    <ArrowDown className="h-3 w-3" aria-hidden="true" />
                                  </button>
                                </div>
                              </div>
                            </td>
                          ) : null}

                          <td className="px-4 py-2.5">
                            <TextField
                              id={`repository-upload-name-${item.id}`}
                              label="Filename"
                              labelHidden
                              value={item.name}
                              disabled={locked}
                              onChange={(event) =>
                                updateItem(batch.id, item.id, { name: event.target.value })
                              }
                              placeholder="File name"
                            />

                            {item.isDuplicate && item.status === 'pending' ? (
                              <p className="mt-1 text-xs text-amber-600">
                                A file of this name is already in {item.folderName}. It will be
                                kept as a separate document.
                              </p>
                            ) : null}

                            {item.error ? (
                              <p className="mt-1 text-xs text-red-700">{item.error}</p>
                            ) : null}
                          </td>

                          <td className="px-4 py-2.5">
                            <Select
                              id={`repository-upload-classification-${item.id}`}
                              ariaLabel="Classification"
                              compact
                              sort="none"
                              disabled={locked}
                              clearable={!item.isControlled}
                              required={item.isControlled}
                              value={item.classificationId}
                              onChange={(next) =>
                                updateItem(batch.id, item.id, { classificationId: next })
                              }
                              options={classifications.map((tier) => ({
                                value: tier.id,
                                label: tier.name,
                              }))}
                              placeholder={item.isControlled ? 'Required' : 'None'}
                            />
                          </td>

                          <td className="whitespace-nowrap px-4 py-2.5 font-mono text-xs tabular-nums text-slate-500">
                            {formatBytes(item.size)}
                          </td>

                          <td className="px-4 py-2.5">
                            <div className="flex flex-col gap-1.5">
                              <StatusText
                                tone={
                                  item.status === 'success'
                                    ? 'active'
                                    : item.status === 'error'
                                      ? 'blocked'
                                      : item.status === 'uploading'
                                        ? 'pending'
                                        : 'inactive'
                                }
                              >
                                {item.status === 'success'
                                  ? 'uploaded'
                                  : item.status === 'error'
                                    ? 'failed'
                                    : item.status === 'uploading'
                                      ? 'uploading'
                                      : 'pending'}
                              </StatusText>

                              {item.status === 'uploading' ? (
                                <div
                                  role="progressbar"
                                  aria-valuenow={item.progress}
                                  aria-valuemin={0}
                                  aria-valuemax={100}
                                  aria-label={`Uploading ${item.name}`}
                                  className="h-1.5 overflow-hidden rounded-full bg-slate-100"
                                >
                                  <div
                                    className="h-full rounded-full bg-blue-500 transition-all"
                                    style={{ width: `${item.progress}%` }}
                                  />
                                </div>
                              ) : null}
                            </div>
                          </td>

                          <td className="px-4 py-2.5">
                            <div className="flex items-center justify-end gap-1.5">
                              {item.status === 'pending' || item.status === 'error' ? (
                                <Tooltip label={item.status === 'error' ? 'Retry' : 'Upload'}>
                                  <button
                                    type="button"
                                    onClick={() => void uploadOne(batch.id, item.id)}
                                    aria-label={item.status === 'error' ? 'Retry' : 'Upload'}
                                    className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                                  >
                                    {item.status === 'error' ? (
                                      <RotateCcw className="h-4 w-4" aria-hidden="true" />
                                    ) : (
                                      <Upload className="h-4 w-4" aria-hidden="true" />
                                    )}
                                  </button>
                                </Tooltip>
                              ) : null}

                              {item.status !== 'uploading' ? (
                                <Tooltip label="Remove from the queue">
                                  <button
                                    type="button"
                                    onClick={() => removeItem(batch.id, item.id)}
                                    aria-label="Remove from the queue"
                                    className="rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"
                                  >
                                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                                  </button>
                                </Tooltip>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      );
                    })}

                    {batch.items.length === 0 ? (
                      <tr>
                        <td
                          colSpan={batch.order === 'sequential' ? 6 : 5}
                          className="px-4 py-10 text-center text-sm text-slate-400"
                        >
                          Every file in this batch has been dealt with.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}

          <div className="flex items-center justify-between border-t border-slate-100 bg-slate-50 px-5 py-2.5">
            {/*
              The business unit is STATED here, once, rather than asked per row. A permanent
              reference number is about to carry it, so the reader should see which unit it
              names — and `SYS` is the honest answer for an account posted to no department,
              which is most of them.
            */}
            <p className="text-xs text-slate-400">
              PDF only · up to {formatBytes(MAX_UPLOAD_BYTES)} per file · split and encrypted
              before storage
              {filedUnder ? (
                <>
                  {' · filed under '}
                  <span className="font-mono text-slate-500">{filedUnder.businessUnitCode}</span>
                  {filedUnder.isSystemFallback ? ' (no department)' : ` — ${filedUnder.orgUnitName}`}
                </>
              ) : null}
            </p>

            <button
              type="button"
              onClick={() => setMinimized(true)}
              className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-800"
            >
              Minimize <ChevronDown className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

    </>,
    document.body,
  );
}
